import { useCallback, useEffect, useRef } from 'react'

import type {
  BodyScrollEvent,
  TooltipHideEvent,
  TooltipShowEvent,
} from 'ag-grid-community'

import type { OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { useManifestTableTracking } from '../../../hooks/useManifestTableTracking'
import {
  computeScrollDepth,
  getTooltipTarget,
  getTooltipTargetKey,
} from './manifestTrackingHelpers'

const SCROLL_DEBOUNCE_MS = 500

/**
 * Fires:
 * - manifest_fullscreen_opened on mount, manifest_fullscreen_closed on unmount
 *   (with time_in_fullscreen_seconds).
 * - manifest_fullscreen_engaged once, on the first click/scroll/tooltip (no
 *   timer — mirrors the inline table).
 * - manifest_fullscreen_scrolled debounced, only on a new max depth in either
 *   scroll_depth_rows (visibleRows.end) or scroll_depth_columns.
 *
 * Also re-fires the generic manifest_tooltip event (column + row_index) on
 * tooltip show — that one isn't variant-specific, so it stays in fullscreen.
 */
export const useManifestFullscreenTracking = (
  listing: OrchestratedListingDetails | null
) => {
  const {
    onManifestFullscreenClosed,
    onManifestFullscreenEngaged,
    onManifestFullscreenOpened,
    onManifestFullscreenScrolled,
    onManifestTooltip,
  } = useManifestTableTracking(listing)

  const containerRef = useRef<HTMLDivElement>(null)
  const openedAtRef = useRef(Date.now()) // oxlint-disable-line react/purity
  const engagedRef = useRef(false)
  const maxRowsRef = useRef(0)
  const maxColsRef = useRef(0)
  const firedRowsRef = useRef(0)
  const firedColsRef = useRef(0)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Indirect open/close through refs so churn in listing/callback identity
  // doesn't tear the mount effect down and re-fire opened/closed.
  const onOpenedRef = useRef(onManifestFullscreenOpened)
  const onClosedRef = useRef(onManifestFullscreenClosed)
  useEffect(() => {
    onOpenedRef.current = onManifestFullscreenOpened
    onClosedRef.current = onManifestFullscreenClosed
  }, [onManifestFullscreenOpened, onManifestFullscreenClosed])

  useEffect(() => {
    const openedAt = openedAtRef.current
    onOpenedRef.current()
    return () => {
      onClosedRef.current(Math.round((Date.now() - openedAt) / 1000))
    }
  }, [])

  const markEngaged = useCallback(() => {
    if (engagedRef.current) return
    engagedRef.current = true
    onManifestFullscreenEngaged()
  }, [onManifestFullscreenEngaged])

  const onBodyScroll = useCallback(
    (e: BodyScrollEvent) => {
      markEngaged()

      const { columns, rows } = computeScrollDepth(e)
      if (columns > maxColsRef.current) maxColsRef.current = columns
      if (rows > maxRowsRef.current) maxRowsRef.current = rows

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        if (
          maxRowsRef.current > firedRowsRef.current ||
          maxColsRef.current > firedColsRef.current
        ) {
          onManifestFullscreenScrolled(maxRowsRef.current, maxColsRef.current)
          firedRowsRef.current = maxRowsRef.current
          firedColsRef.current = maxColsRef.current
        }
      }, SCROLL_DEBOUNCE_MS)
    },
    [markEngaged, onManifestFullscreenScrolled]
  )

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    },
    []
  )

  // ag-Grid can re-fire tooltipShow for a cell whose tooltip is already
  // showing (e.g. on a grid re-render, or a small pointer move within the
  // same cell) — this ref suppresses those repeats, and is cleared on
  // tooltipHide so the next genuine hover fires again.
  const lastTooltipTargetRef = useRef<string | null>(null)

  const onTooltipShow = useCallback(
    (e?: TooltipShowEvent) => {
      markEngaged()
      const { column, rowIndex } = getTooltipTarget(e)
      const targetKey = getTooltipTargetKey(column, rowIndex)
      if (targetKey === lastTooltipTargetRef.current) return
      lastTooltipTargetRef.current = targetKey
      onManifestTooltip(column, rowIndex)
    },
    [markEngaged, onManifestTooltip]
  )

  const onTooltipHide = useCallback((_e?: TooltipHideEvent) => {
    lastTooltipTargetRef.current = null
  }, [])

  return {
    containerRef,
    onContainerClick: markEngaged,
    agGridProps: { onBodyScroll, onTooltipHide, onTooltipShow },
  }
}
